/**
 * Adjust cognitive game level timing when patient has "relaxed" (more time) mode.
 * Analytics difficulty is unchanged; only client-side caps/pacing differ.
 */

import { GAME_IDS, type GameId } from "@models/game";

export type GameTimingMode = "standard" | "relaxed";

/**
 * Fixed per-game bonus granted when the player taps the in-game "more time"
 * control (once per logical step: problem/round/trial). Kept in code so we
 * don't expose a slider to caregivers; only on/off is configurable.
 */
export const IN_GAME_MORE_TIME_BONUS_MS: Partial<Record<GameId, number>> = {
  [GAME_IDS.mathBlitz]: 15_000,
  [GAME_IDS.wordFluencyTap]: 15_000,
  [GAME_IDS.targetFocus]: 10_000,
  [GAME_IDS.patternMatch]: 15_000,
  [GAME_IDS.categoryTap]: 5_000,
  [GAME_IDS.reactionTap]: 2_000,
  [GAME_IDS.nBack]: 1_500,
  [GAME_IDS.pathMemory]: 3_000,
};

/** Returns the bonus (ms) for a game, or 0 if the game has no configured bonus. */
export function getInGameMoreTimeBonusMs(gameId: GameId): number {
  return IN_GAME_MORE_TIME_BONUS_MS[gameId] ?? 0;
}

/** Multiplier applied to ms-based caps and round durations in relaxed mode. */
export const RELAXED_TIME_MULTIPLIER = 1.5;

/** Default ISI between N-Back stimuli (ms); scaled in relaxed mode. */
export const NBACK_ISI_MS_STANDARD = 500;

function scaleMs(value: number, mode: GameTimingMode): number {
  if (mode !== "relaxed") return value;
  return Math.max(1, Math.round(value * RELAXED_TIME_MULTIPLIER));
}

function scaleSeconds(value: number, mode: GameTimingMode): number {
  if (mode !== "relaxed") return value;
  return Math.max(1, Math.round(value * RELAXED_TIME_MULTIPLIER));
}

export function resolveGameTimingMode(
  raw: string | undefined | null
): GameTimingMode {
  return raw === "relaxed" ? "relaxed" : "standard";
}

export function adjustMathBlitzTiming<T extends { timePerProblemMs: number }>(
  params: T,
  mode: GameTimingMode
): T {
  if (mode !== "relaxed") return params;
  return {
    ...params,
    timePerProblemMs: scaleMs(params.timePerProblemMs, mode),
  };
}

export function adjustWordFluencyTapTiming<T extends { timeSeconds: number }>(
  params: T,
  mode: GameTimingMode
): T {
  if (mode !== "relaxed") return params;
  return {
    ...params,
    timeSeconds: scaleSeconds(params.timeSeconds, mode),
  };
}

export function adjustTargetFocusTiming<
  T extends { roundDurationMs: number },
>(params: T, mode: GameTimingMode): T {
  if (mode !== "relaxed") return params;
  return {
    ...params,
    roundDurationMs: scaleMs(params.roundDurationMs, mode),
  };
}

export function adjustPatternMatchTiming<T extends { timeSeconds: number }>(
  params: T,
  mode: GameTimingMode
): T {
  if (mode !== "relaxed" || params.timeSeconds <= 0) return params;
  return {
    ...params,
    timeSeconds: scaleSeconds(params.timeSeconds, mode),
  };
}

export function adjustCategoryTapTiming<T extends { timePerItemMs: number }>(
  params: T,
  mode: GameTimingMode
): T {
  if (mode !== "relaxed" || params.timePerItemMs <= 0) return params;
  return {
    ...params,
    timePerItemMs: scaleMs(params.timePerItemMs, mode),
  };
}

export function adjustReactionTapTiming<
  T extends {
    maxReactionMs: number;
    minDelayMs: number;
    maxDelayMs: number;
  },
>(params: T, mode: GameTimingMode): T {
  if (mode !== "relaxed") return params;
  return {
    ...params,
    maxReactionMs: scaleMs(params.maxReactionMs, mode),
    minDelayMs: scaleMs(params.minDelayMs, mode),
    maxDelayMs: scaleMs(params.maxDelayMs, mode),
  };
}

export type NBackLevelTimingInput = {
  displayMs: number;
  n: number;
  sequenceLength: number;
  showLabels: boolean;
  showHistory: boolean;
};

/** N-Back: display duration per stimulus + ISI (defaults scaled from isiMsStandard). */
export function adjustNBackTiming(
  params: NBackLevelTimingInput,
  mode: GameTimingMode,
  isiMsStandard: number = NBACK_ISI_MS_STANDARD
): { params: NBackLevelTimingInput; isiMs: number } {
  if (mode !== "relaxed") {
    return { params, isiMs: isiMsStandard };
  }
  return {
    params: {
      ...params,
      displayMs: scaleMs(params.displayMs, mode),
    },
    isiMs: scaleMs(isiMsStandard, mode),
  };
}

export function adjustPathMemoryTiming<T extends { displayMs: number }>(
  params: T,
  mode: GameTimingMode
): T {
  if (mode !== "relaxed") return params;
  return {
    ...params,
    displayMs: scaleMs(params.displayMs, mode),
  };
}
